import { AccountSettings } from "01Data/Account";
import { AppContext } from "01Data/AppData";
import Copyright from "03AdminPages/99Parts/Copyright";
import { PasswordPolicy } from "97Parts/Styles";
import { useSignal } from "99Tools/useModel";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Container,
  CssBaseline,
  FormControlLabel,
  TextField,
  Typography,
  styled,
} from "@mui/material";
import { validate } from "email-validator";
import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";

const MyPaper = styled("div")(({ theme }) => ({
  marginTop: theme.spacing(8),
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
}));

const MyAvatar = styled(Avatar)(({ theme }) => ({
  margin: theme.spacing(1),
  backgroundColor: theme.palette.secondary.main,
}));

const MyForm = styled("form")(({ theme }) => ({
  width: "100%", // Fix IE 11 issue.
  marginTop: theme.spacing(1),
}));

const ErrorText = styled("div")(({ theme }) => ({
  color: "#f00",
}));

const MessageText = styled("div")(({ theme }) => ({
  color: "#00f",
}));

const ResendButton = styled(Button)(({ theme }) => ({
  margin: theme.spacing(3, 0, 0),
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  margin: theme.spacing(3, 0, 2),
}));

const DescriptionText = styled("div")(({ theme }) => ({
  fontSize: "0.95rem",
  color: theme.palette.grey[800],
  margin: theme.spacing(2, 0, 2),
}));

const AgreeEulaNote = styled("div")(({ theme }) => ({
  fontSize: "0.95rem",
  color: theme.palette.grey[800],
  margin: theme.spacing(2, 0, 2),
}));

const RegisterPage = () => {
  const a = useContext(AppContext).account;
  const navigate = useNavigate();
  const [account] = useState<AccountSettings>(new AccountSettings());
  const [showPwd, setShowPwd] = useState(false);
  const [agreeEula, setAgreeEula] = useState(false);
  const [step, setStep] = useState<"form" | "confirm">("form");
  const [message, setMessage] = useState("");
  const [info, setInfo] = useState("");
  const redraw = useSignal();

  const handleSubmit = () => {
    if (account.companyName === "") {
      setMessage("法人名を入力してください。");
      return;
    }
    if (account.lastName === "") {
      setMessage("担当者名 姓を入力してください。");
      return;
    }
    if (account.firstName === "") {
      setMessage("担当者名 名を入力してください。");
      return;
    }
    if (account.department === "") {
      setMessage("部署名を入力してください。");
      return;
    }
    if (account.companyPhone === "") {
      setMessage("電話番号を入力してください。");
      return;
    }
    if (account.companyPhone.match(/^0[0-9]{9,10}$/) === null) {
      setMessage("有効な電話番号を入力してください。");
      return;
    }
    if (account.user === "") {
      setMessage("メールアドレスを入力してください。");
      return;
    }
    if (!validate(account.user)) {
      setMessage("有効なメールアドレスを入力してください。");
      return;
    }
    if (account.pass === "") {
      setMessage("パスワードを入力してください。");
      return;
    }
    if (account.passConfirm === "") {
      setMessage("確認用パスワードを入力してください。");
      return;
    }
    if (
      account.pass.match(/^(?=.*?[a-z])(?=.*?[A-Z])(?=.*?\d).{8,}$/) == null
    ) {
      setMessage(
        "パスワードは8文字以上で大文字・小文字・数字をそれぞれ含む必要があります。"
      );
      return;
    }
    if (account.pass !== account.passConfirm) {
      setMessage("現在のパスワードと確認用パスワードが一致しません。");
      return;
    }

    a.SignUp(account)
      .then(() => {
        setStep("confirm");
      })
      .catch((err: Error) => {
        if (0 <= err.message.toLowerCase().indexOf("already exists")) {
          // エラーメッセージが変更されたら対応が必要
          setMessage("アカウントはすでに登録されています。");
        } else if (0 <= err.message.toLowerCase().indexOf("validation error")) {
          setMessage(
            "メールアドレスに誤りがあります。入力内容の確認をお願いします。"
          );
        } else {
          setMessage(
            "サインアップエラーが発生しました。管理者に連絡してください。"
          );
        }
        console.log("サインアップエラー");
        console.error(err);
      });
  };

  const handleSubmitCode = () => {
    setInfo("");
    if (account.code === "") {
      setMessage("コードを入力してください。");
      return;
    }
    a.ConfirmSignUp(account)
      .then(() => {
        navigate("/login/");
      })
      .catch((err: any) => {
        setMessage(
          "コードが誤っているか有効期限切れです。訂正して再送するか新しいコードを発行してください。"
        );
        console.log("コード確認エラー");
        console.error(err);
      });
  };

  const handleResendCode = () => {
    setMessage("");
    a.ResendConfirmCode(account)
      .then(() => {
        setInfo("コードを再送信しました。");
      })
      .catch((err: any) => {
        setMessage("コードの再送信に失敗しました。管理者にご連絡ください。");
        console.log("コード再送信エラー");
        console.error(err);
      });
  };

  const handleChangeLastName = (e: any) => {
    account.lastName = e.target.value;
    setMessage("");
    redraw();
  };

  const handleChangeFirstName = (e: any) => {
    account.firstName = e.target.value;
    setMessage("");
    redraw();
  };

  const handleChangeMail = (e: any) => {
    account.user = e.target.value;
    setMessage("");
    redraw();
  };

  const handleChangePass = (e: any) => {
    account.pass = e.target.value;
    setMessage("");
    redraw();
  };

  const handleChangePassConfirm = (e: any) => {
    account.passConfirm = e.target.value;
    setMessage("");
    redraw();
  };

  const handleShowPwd = (e: any) => {
    setMessage("");
    setShowPwd(e.target.checked);
  };

  const handleAgreeEula = (e: any) => {
    setAgreeEula(e.target.checked);
  };

  const handleChangeCode = (e: any) => {
    account.code = e.target.value;
    setMessage("");
    redraw();
  };

  const handleChangeCompanyName = (e: any) => {
    account.companyName = e.target.value;
    setMessage("");
    redraw();
  };

  const handleChangeDepartment = (e: any) => {
    account.department = e.target.value;
    setMessage("");
    redraw();
  };

  const handleChangePhone = (e: any) => {
    account.companyPhone = e.target.value;
    setMessage("");
    redraw();
  };
  const form = (
    <>
      <Typography component="h1" variant="h5">
        アカウント作成
      </Typography>
      <MyForm noValidate>
        <TextField
          autoFocus
          required
          fullWidth
          variant="outlined"
          margin="normal"
          id="companyName"
          name="companyNname"
          label="法人名"
          inputProps={{ maxLength: 128 }}
          value={account.companyName}
          onChange={handleChangeCompanyName}
        />
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <TextField
            required
            variant="outlined"
            margin="normal"
            id="last"
            name="last"
            label="担当者名 姓"
            sx={{ width: "48%" }}
            inputProps={{ maxLength: 128 }}
            value={account.lastName}
            onChange={handleChangeLastName}
          />
          <TextField
            required
            variant="outlined"
            margin="normal"
            id="first"
            name="first"
            label="担当者名 名"
            sx={{ width: "48%" }}
            inputProps={{ maxLength: 128 }}
            value={account.firstName}
            onChange={handleChangeFirstName}
          />
        </div>
        <TextField
          required
          fullWidth
          variant="outlined"
          margin="normal"
          id="department"
          name="department"
          label="部署名"
          inputProps={{ maxLength: 128 }}
          value={account.department}
          onChange={handleChangeDepartment}
        />
        <TextField
          required
          fullWidth
          variant="outlined"
          margin="normal"
          id="companyPhone"
          name="companyPhone"
          label="電話番号"
          type="tel"
          autoComplete="tel"
          inputProps={{ maxLength: 11 }}
          value={account.companyPhone}
          onChange={handleChangePhone}
        />
        <PasswordPolicy>※電話番号は数字で入力してください。</PasswordPolicy>
        <TextField
          required
          fullWidth
          variant="outlined"
          margin="normal"
          id="email"
          name="email"
          label="メールアドレス"
          autoComplete="email"
          inputProps={{ maxLength: 128 }}
          value={account.user}
          onChange={handleChangeMail}
        />
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <TextField
            required
            variant="outlined"
            margin="normal"
            id="password"
            name="password"
            label="パスワード"
            autoComplete="new-password"
            type={showPwd ? "text" : "password"}
            sx={{ width: "48%" }}
            inputProps={{ maxLength: 128 }}
            value={account.pass}
            onChange={handleChangePass}
          />
          <TextField
            required
            variant="outlined"
            margin="normal"
            id="passwordConfirm"
            name="password"
            label="確認用パスワード"
            autoComplete="new-password"
            type={showPwd ? "text" : "password"}
            sx={{ width: "48%" }}
            inputProps={{ maxLength: 128 }}
            value={account.passConfirm}
            onChange={handleChangePassConfirm}
          />
        </div>
        <PasswordPolicy>
          ※半角英大文字・半角英小文字・数字を含む、８文字以上のパスワードを設定してください。
        </PasswordPolicy>
        <FormControlLabel
          control={<Checkbox onChange={handleShowPwd} />}
          label="パスワードを表示します"
        />
        <hr />
        <Typography variant="h6" color="text:secondary">
          利用規約
        </Typography>
        <AgreeEulaNote>
          ご利用にあたり、
          <a
            href={process.env.REACT_APP_URL_EULA}
            target="_blank"
            rel="noreferrer"
          >
            利用規約
          </a>
          への同意が必要です。
          <br />
          規約に同意いただける場合は、以下のチェックボックスにチェックを入れた上で次へお進みください。
        </AgreeEulaNote>
        <FormControlLabel
          control={<Checkbox onChange={handleAgreeEula} />}
          label={<span>利用規約に同意します</span>}
        />
        {0 < message.length ? <ErrorText>{message}</ErrorText> : null}
        {agreeEula ? (
          <SubmitButton
            fullWidth
            variant="contained"
            color="primary"
            onClick={handleSubmit}
          >
            次へ
          </SubmitButton>
        ) : (
          <SubmitButton fullWidth variant="contained" color="primary" disabled>
            次へ
          </SubmitButton>
        )}
      </MyForm>
    </>
  );

  const confirm = (
    <>
      <Typography component="h1" variant="h5">
        認証コードの入力
      </Typography>
      <MyForm noValidate>
        <DescriptionText>{`${account.user}宛にお送りした認証コードを入力ください。見つからない場合は、迷惑メールフォルダをご確認ください`}</DescriptionText>
        <TextField
          variant="outlined"
          margin="normal"
          required
          fullWidth
          id="code"
          label="コードを入力"
          name="code"
          autoComplete="off"
          inputProps={{ maxLength: 128 }}
          value={account.code}
          onChange={handleChangeCode}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleSubmitCode();
            }
          }}
        />
        {0 < message.length ? <ErrorText>{message}</ErrorText> : null}
        <ResendButton variant="text" type="button" onClick={handleResendCode}>
          コードを再送信する
        </ResendButton>
        {0 < info.length ? <MessageText>{info}</MessageText> : null}
        <SubmitButton
          fullWidth
          type="button"
          variant="contained"
          color="primary"
          onClick={handleSubmitCode}
        >
          登録
        </SubmitButton>
      </MyForm>
    </>
  );

  return (
    <Container component="main" maxWidth="sm">
      <CssBaseline />
      <MyPaper>
        <Typography component="h1" variant="h5">
          漏水みはり番監視システム
        </Typography>
        <MyAvatar>
          <LockOutlinedIcon />
        </MyAvatar>
        {step === "form" ? form : confirm}
      </MyPaper>
      <Box mt={8}>
        <Copyright />
      </Box>
    </Container>
  );
};

export default RegisterPage;
